/**
 * The small status/priority/spec pills used across every tab.
 *
 * Generalises the two-state badge in Lessons/components/StatusBadge.tsx to the five statuses
 * this tracker uses.
 */
import { AlertTriangle, HelpCircle } from 'lucide-react';
import {
  STATUS_LABELS,
  type HipaaEffort,
  type HipaaPriority,
  type HipaaSpec,
  type HipaaStatus,
} from '../types/hipaaTypes';

const PILL = 'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full';

const STATUS_CLASSES: Record<HipaaStatus, string> = {
  not_started: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  partial: 'bg-amber-100 text-amber-800',
  done: 'bg-green-100 text-green-700',
  na: 'bg-slate-100 text-slate-500',
};

export const StatusPill = ({ status, derived = false }: { status: HipaaStatus; derived?: boolean }) => (
  <span className={`${PILL} ${STATUS_CLASSES[status]}`} title={derived ? 'Derived from the sections this control maps to' : undefined}>
    {STATUS_LABELS[status]}
    {derived && <span className="opacity-60">auto</span>}
  </span>
);

// P0 is the gate on the first BAA, so it is the only one that reads as urgent.
const PRIORITY_CLASSES: Record<HipaaPriority, string> = {
  P0: 'bg-red-100 text-red-700',
  P1: 'bg-orange-100 text-orange-700',
  P2: 'bg-gray-100 text-gray-600',
};

export const PriorityBadge = ({ priorities }: { priorities: HipaaPriority[] }) => {
  if (priorities.length === 0) return null;

  return (
    <span className={`${PILL} ${PRIORITY_CLASSES[priorities[0]]}`}>{priorities.join('/')}</span>
  );
};

export const SpecBadge = ({ specs }: { specs: HipaaSpec[] }) => {
  if (specs.length === 0) return null;

  return (
    <span
      className={`${PILL} bg-purple-100 text-purple-700`}
      title={
        specs.includes('R')
          ? 'Required implementation spec — not optional'
          : 'Addressable — implement it, or document why an alternative is reasonable (§164.306(d))'
      }
    >
      {specs.join(' + ')}
    </span>
  );
};

export const EffortBadge = ({ effort }: { effort: HipaaEffort | null }) => {
  if (!effort) return null;

  return <span className={`${PILL} bg-indigo-100 text-indigo-700`}>{effort}</span>;
};

export const BlockedBadge = ({ reason }: { reason: string }) => (
  <span className={`${PILL} bg-red-100 text-red-700`} title={reason || 'Blocked'}>
    <AlertTriangle size={11} />
    Blocked
  </span>
);

export const VerificationBadge = () => (
  <span
    className={`${PILL} bg-yellow-100 text-yellow-800`}
    title="Cannot be settled from the repo — needs a console or vendor check"
  >
    <HelpCircle size={11} />
    Verify
  </span>
);
